import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileJson, Upload } from 'lucide-react';
import { isTrelloExport, summarizeTrello, type TrelloSummary } from '@kaba/shared';
import { api } from '../lib/api';
import { useMe } from '../lib/auth';
import { queryClient } from '../lib/board';
import { navigate } from '../lib/router';
import { toast } from '../lib/toast';
import { refreshStructure, useNav, type Target } from '../lib/structure';
import { PlacementSelect } from './home/PlacementSelect';
import { Button, cx } from './ui/ui';

const field = 'min-h-11 w-full rounded-lg border border-[var(--line)] bg-[var(--panel)] px-3 text-base sm:min-h-10 sm:text-sm';
const n = (x: number, one: string, many: string) => `${x.toLocaleString('de-DE')} ${x === 1 ? one : many}`;

interface Contact {
  id: string;
  name: string;
  email: string;
}

/** Board aus einem Trello-JSON-Export anlegen (Reiter „Trello“ im Import von „Neues Board“) */
export function ImportTrelloForm({ open, onClose, onCancel, target }: { open: boolean; onClose: () => void; onCancel?: () => void; target?: Target | null }) {
  const { data: me } = useMe();
  const { data: contacts } = useQuery({ queryKey: ['import-contacts'], queryFn: () => api.get<Contact[]>('/api/import/contacts'), enabled: open });
  const [file, setFile] = useState<File | null>(null);
  const [summary, setSummary] = useState<TrelloSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [comments, setComments] = useState(true);
  const [archived, setArchived] = useState(false);
  const [map, setMap] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const { data: nav } = useNav(open);
  const [place, setPlace] = useState<Target>({ areaId: null, folderId: null });
  useEffect(() => {
    if (open) setPlace(target ?? { areaId: null, folderId: null });
  }, [open, target]);

  useEffect(() => {
    if (!open) {
      setFile(null);
      setSummary(null);
      setError(null);
      setMap({});
    }
  }, [open]);

  const choose = async (f: File | null) => {
    setError(null);
    setSummary(null);
    setFile(f);
    if (!f) return;
    try {
      const data: unknown = JSON.parse(await f.text());
      if (!isTrelloExport(data)) throw new Error('Das sieht nicht nach einem Trello-Export aus. Bitte die JSON-Datei aus Trello wählen.');
      const s = summarizeTrello(data);
      setSummary(s);
      setTitle(s.boardName);
      // Gleiche Namen automatisch zuordnen
      const auto: Record<string, string> = {};
      for (const m of s.members) {
        const hit = contacts?.find((c) => c.name.trim().toLowerCase() === m.name.trim().toLowerCase());
        if (hit) auto[m.key] = hit.id;
      }
      setMap(auto);
    } catch (err) {
      setError(err instanceof SyntaxError ? 'Die Datei ist kein gültiges JSON.' : (err as Error).message);
    }
  };

  return (
      <form
        className="flex flex-col gap-4"
        onSubmit={async (e) => {
          e.preventDefault();
          if (!file || !summary) return;
          setBusy(true);
          try {
            const form = new FormData();
            form.set('file', file);
            const memberMap: Record<string, string | null> = {};
            for (const m of summary.members) memberMap[m.key] = map[m.key] || null;
            form.set(
              'options',
              JSON.stringify({ title: title.trim(), includeComments: comments, includeArchived: archived, memberMap, areaId: place.areaId, folderId: place.folderId }),
            );
            const r = await api.upload<{ boardId: string; cards: number; comments: number }>('/api/import/trello', form);
            await queryClient.invalidateQueries({ queryKey: ['boards'] });
            refreshStructure();
            toast.info(`Importiert: ${n(r.cards, 'Karte', 'Karten')}${r.comments ? `, ${n(r.comments, 'Kommentar', 'Kommentare')}` : ''}.`);
            onClose();
            navigate(`/b/${r.boardId}`);
          } catch (err) {
            toast.error((err as Error).message);
          } finally {
            setBusy(false);
          }
        }}
      >
        <ol className="list-decimal space-y-1 pl-5 text-sm text-[var(--ink-soft)]">
          <li>In Trello das Board öffnen → Menü (⋯) → „Drucken, Exportieren und Teilen“ → „Als JSON exportieren“.</li>
          <li>Die Seite mit Strg/Cmd+S als Datei speichern (am Handy: Teilen → In Dateien sichern).</li>
          <li>Die Datei hier auswählen. Dein Trello-Board bleibt unverändert.</li>
        </ol>

        <label
          className={cx(
            'flex min-h-20 cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-dashed p-4 text-sm',
            summary ? 'border-accent bg-accent-soft/40' : 'border-[var(--line)] hover:bg-black/[0.02] dark:hover:bg-white/5',
          )}
        >
          <input type="file" accept=".json,application/json" className="sr-only" onChange={(e) => void choose(e.target.files?.[0] ?? null)} aria-label="Trello-Export auswählen" />
          {file ? <FileJson size={20} /> : <Upload size={20} />}
          <span className="truncate">{file ? file.name : 'JSON-Datei auswählen'}</span>
        </label>
        {error && (
          <p role="alert" className="rounded-lg bg-rose-50 p-2.5 text-sm text-rose-700 dark:bg-rose-950 dark:text-rose-200">
            {error}
          </p>
        )}

        {summary && (
          <>
            <p className="rounded-xl bg-[var(--column)] p-3 text-sm">
              <strong>„{summary.boardName}“</strong>: {n(summary.lists, 'Liste', 'Listen')}, {n(summary.cards, 'Karte', 'Karten')}, {n(summary.labels, 'Label', 'Labels')},{' '}
              {n(summary.checklistItems, 'Checklistenpunkt', 'Checklistenpunkte')}, {n(summary.comments, 'Kommentar', 'Kommentare')}.
            </p>
            <label className="flex flex-col gap-1.5 text-sm font-medium">
              Name des neuen Boards
              <input className={field} required maxLength={200} value={title} onChange={(e) => setTitle(e.target.value)} />
            </label>
            {!!nav?.areas.length && (
              <div className="flex flex-col gap-1.5 text-sm font-medium">
                <label htmlFor="import-place">Ablage</label>
                <PlacementSelect id="import-place" nav={nav} value={place} onChange={setPlace} />
              </div>
            )}
            <div className="flex flex-col gap-1">
              <label className="flex min-h-11 items-center gap-3 text-sm">
                <input type="checkbox" checked={comments} onChange={(e) => setComments(e.target.checked)} className="size-5 accent-[var(--color-accent)]" />
                Kommentare übernehmen
              </label>
              {summary.archivedCards > 0 && (
                <label className="flex min-h-11 items-center gap-3 text-sm">
                  <input type="checkbox" checked={archived} onChange={(e) => setArchived(e.target.checked)} className="size-5 accent-[var(--color-accent)]" />
                  {n(summary.archivedCards, 'archivierte Karte', 'archivierte Karten')} mitnehmen (bleiben archiviert)
                </label>
              )}
            </div>

            {summary.members.length > 0 && (
              <fieldset className="flex flex-col gap-2">
                <legend className="mb-1 text-sm font-medium">Personen zuordnen</legend>
                <p className="text-xs text-[var(--ink-soft)]">
                  Zugeordnete Personen werden zuständig für ihre Karten, ihre Kommentare erscheinen unter ihrem Namen, und sie kommen als Bearbeiter ins
                  Board. Ohne Zuordnung bleibt der Name im Kommentartext erhalten.
                </p>
                {summary.members.map((m) => (
                  <label key={m.key} className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="min-w-32 flex-1 truncate">
                      {m.name} {m.username && <span className="text-xs text-[var(--ink-soft)]">@{m.username}</span>}
                    </span>
                    <select
                      aria-label={`${m.name} zuordnen`}
                      className={cx(field, 'w-auto min-w-48 flex-1')}
                      value={map[m.key] ?? ''}
                      onChange={(e) => setMap((x) => ({ ...x, [m.key]: e.target.value }))}
                    >
                      <option value="">nicht zuordnen</option>
                      {contacts?.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.id === me?.id ? `${c.name} (ich)` : c.name}
                        </option>
                      ))}
                    </select>
                  </label>
                ))}
              </fieldset>
            )}

            <p className="text-xs text-[var(--ink-soft)]">
              Nicht übernommen werden: hochgeladene Dateien (sie stehen als Link in der Beschreibung; zum Öffnen braucht es weiterhin Trello), Power-Ups
              {summary.customFields ? ` und ${n(summary.customFields, 'eigenes Feld', 'eigene Felder')}` : ''}
              {summary.closedLists ? `, ${n(summary.closedLists, 'archivierte Liste', 'archivierte Listen')}` : ''}. Trello gibt im Export höchstens die
              letzten 1000 Aktionen mit – bei sehr alten Boards können frühe Kommentare fehlen.
            </p>
          </>
        )}

        <div className="flex justify-end gap-2">
          {onCancel && (
            <Button type="button" onClick={onCancel}>
              Abbrechen
            </Button>
          )}
          <Button type="submit" variant="primary" disabled={!summary || !title.trim() || busy}>
            {busy ? 'Importiere …' : 'Importieren'}
          </Button>
        </div>
      </form>
  );
}
